import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AppointmentFormProps, AppointmentFormData, CalendarWorkingSettings } from './types';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { clientService, ClientDTO } from '../../services/clientApi';
import {
  CALENDAR_SETTINGS,
  roundDateToBookingStep,
} from '../../config/calendarSettings';
import {
  DEFAULT_WORKDAY_START_TIME,
  DEFAULT_WORKDAY_END_TIME,
  DEFAULT_BREAK_START_TIME,
  DEFAULT_BREAK_END_TIME,
} from '../../config/workingTimeDefaults';
import { getUserPreferences, subscribeUserPreferences } from '../../services/userPreferences';

const RECENT_CLIENTS_STORAGE_KEY = 'appointmentRecentClientIds';
const MAX_RECENT_CLIENTS = 6;

interface WorkingRange {
  workdayStartMinutes: number;
  workdayEndMinutes: number;
  enableMiddayBreak: boolean;
  breakStartMinutes: number;
  breakEndMinutes: number;
}

const parseRecentClientIds = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
};

const readRecentClientIds = (): string[] => {
  if (globalThis.window === undefined) {
    return [];
  }

  return parseRecentClientIds(globalThis.window.localStorage.getItem(RECENT_CLIENTS_STORAGE_KEY));
};

const writeRecentClientIds = (ids: string[]) => {
  if (globalThis.window === undefined) {
    return;
  }

  globalThis.window.localStorage.setItem(RECENT_CLIENTS_STORAGE_KEY, JSON.stringify(ids));
};

const getClientSearchText = (client: ClientDTO): string => {
  return [client.firstName, client.lastName, client.company, client.city, client.phone]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const parseTimeToMinutes = (value: string): number | null => {
  const parts = value.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const formatMinutes = (value: number): string => {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, value));
  const hours = Math.floor(clamped / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (clamped % 60).toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const resolveWorkingRange = (workingSettings?: CalendarWorkingSettings): WorkingRange => {
  const fallbackStartMinutes = CALENDAR_SETTINGS.workStartHour * 60;
  const fallbackEndMinutes = CALENDAR_SETTINGS.workEndHour * 60;

  const workdayStartMinutes =
    parseTimeToMinutes(workingSettings?.workdayStartTime || DEFAULT_WORKDAY_START_TIME) ??
    fallbackStartMinutes;

  const requestedWorkdayEndMinutes =
    parseTimeToMinutes(workingSettings?.workdayEndTime || DEFAULT_WORKDAY_END_TIME) ??
    fallbackEndMinutes;

  const workdayEndMinutes =
    requestedWorkdayEndMinutes > workdayStartMinutes
      ? requestedWorkdayEndMinutes
      : fallbackEndMinutes;

  const requestedBreakStartMinutes = parseTimeToMinutes(
    workingSettings?.breakStartTime || DEFAULT_BREAK_START_TIME,
  );
  const requestedBreakEndMinutes = parseTimeToMinutes(
    workingSettings?.breakEndTime || DEFAULT_BREAK_END_TIME,
  );

  const hasValidBreak =
    Boolean(workingSettings?.enableMiddayBreak) &&
    requestedBreakStartMinutes !== null &&
    requestedBreakEndMinutes !== null &&
    requestedBreakEndMinutes > requestedBreakStartMinutes &&
    requestedBreakStartMinutes >= workdayStartMinutes &&
    requestedBreakEndMinutes <= workdayEndMinutes;

  return {
    workdayStartMinutes,
    workdayEndMinutes,
    enableMiddayBreak: hasValidBreak,
    breakStartMinutes: hasValidBreak ? requestedBreakStartMinutes : 0,
    breakEndMinutes: hasValidBreak ? requestedBreakEndMinutes : 0,
  };
};

const isInBreak = (minutes: number, range: WorkingRange): boolean => {
  return (
    range.enableMiddayBreak &&
    minutes >= range.breakStartMinutes &&
    minutes < range.breakEndMinutes
  );
};

const clampDateToWorkingRangeForSettings = (date: Date, range: WorkingRange): Date => {
  const clamped = new Date(date);
  const maxStartMinutes = Math.max(
    range.workdayStartMinutes,
    range.workdayEndMinutes - CALENDAR_SETTINGS.bookingStepMinutes,
  );
  const currentMinutes = clamped.getHours() * 60 + clamped.getMinutes();

  let targetMinutes = currentMinutes;

  if (targetMinutes < range.workdayStartMinutes) {
    targetMinutes = range.workdayStartMinutes;
  }

  if (targetMinutes > maxStartMinutes) {
    targetMinutes = maxStartMinutes;
  }

  if (isInBreak(targetMinutes, range)) {
    targetMinutes = Math.min(range.breakEndMinutes, maxStartMinutes);

    if (isInBreak(targetMinutes, range)) {
      targetMinutes = Math.max(
        range.workdayStartMinutes,
        range.breakStartMinutes - CALENDAR_SETTINGS.bookingStepMinutes,
      );
    }
  }

  clamped.setHours(0, 0, 0, 0);
  clamped.setMinutes(targetMinutes);
  return clamped;
};

const buildStartTimeOptions = (range: WorkingRange): string[] => {
  const options: string[] = [];
  const maxStartMinutes = Math.max(
    range.workdayStartMinutes,
    range.workdayEndMinutes - CALENDAR_SETTINGS.bookingStepMinutes,
  );

  for (
    let minutes = range.workdayStartMinutes;
    minutes <= maxStartMinutes;
    minutes += CALENDAR_SETTINGS.bookingStepMinutes
  ) {
    if (isInBreak(minutes, range)) {
      continue;
    }

    options.push(formatMinutes(minutes));
  }

  return options;
};

const getDefaultStartTime = (workingSettings?: CalendarWorkingSettings) => {
  const roundedNow = roundDateToBookingStep(new Date());
  const range = resolveWorkingRange(workingSettings);
  return clampDateToWorkingRangeForSettings(roundedNow, range);
};

const getDefaultVisitDuration = (): number => {
  const preferredDuration = getUserPreferences().defaultVisitDuration;
  return Number.isFinite(preferredDuration) && preferredDuration > 0
    ? Math.round(preferredDuration)
    : CALENDAR_SETTINGS.defaultAppointmentDurationMinutes;
};

const formatDurationLabel = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return hours === 1 ? '1 heure' : `${hours} heures`;
  }

  const hoursLabel = hours === 1 ? '1 heure' : `${hours} heures`;
  return `${hoursLabel} ${remainingMinutes}`;
};

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  isOpen,
  appointment,
  initialData,
  workingSettings,
  onSubmit,
  onDelete,
  onCancel,
  isLoading = false
}) => {
  const workingRange = useMemo(() => resolveWorkingRange(workingSettings), [workingSettings]);

  const initialStartTime = useMemo(
    () => getDefaultStartTime(workingSettings),
    [workingSettings]
  );

  const [formData, setFormData] = useState<AppointmentFormData>({
    clientId: '',
    startTime: initialStartTime,
    endTime: new Date(initialStartTime.getTime() + getDefaultVisitDuration() * 60 * 1000),
    duration: getDefaultVisitDuration(),
    title: '',
    description: '',
    notes: '',
    status: 'scheduled'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [durationOptions, setDurationOptions] = useState<number[]>(
    () => getUserPreferences().appointmentDurationOptions,
  );
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [recentClientIds, setRecentClientIds] = useState<string[]>(() => readRecentClientIds());
  // true = le titre a été généré automatiquement et peut être écrasé lors d'un changement de client
  const isAutoTitle = useRef<boolean>(true);

  const clientsWithId = useMemo(
    () => clients.filter((client): client is ClientDTO & { id: string } => typeof client.id === 'string' && client.id.length > 0),
    [clients]
  );

  const filteredClients = useMemo(() => {
    const query = clientSearchQuery.trim().toLowerCase();

    if (!query) {
      return clientsWithId;
    }

    return clientsWithId.filter((client) => getClientSearchText(client).includes(query));
  }, [clientsWithId, clientSearchQuery]);

  const filteredClientIdSet = useMemo(
    () => new Set(filteredClients.map((client) => client.id)),
    [filteredClients]
  );

  const recentClients = useMemo(() => {
    return recentClientIds
      .map((id) => clientsWithId.find((client) => client.id === id))
      .filter((client): client is ClientDTO & { id: string } => Boolean(client))
      .filter((client) => filteredClientIdSet.has(client.id));
  }, [clientsWithId, recentClientIds, filteredClientIdSet]);

  const recentClientIdSet = useMemo(
    () => new Set(recentClients.map((client) => client.id)),
    [recentClients]
  );

  const otherFilteredClients = useMemo(
    () => filteredClients.filter((client) => !recentClientIdSet.has(client.id)),
    [filteredClients, recentClientIdSet]
  );

  const durationOptionsForSelect = useMemo(() => {
    const merged = [...durationOptions, formData.duration];
    return Array.from(new Set(merged)).sort((left, right) => left - right);
  }, [durationOptions, formData.duration]);

  // Initialiser le formulaire
  useEffect(() => {
    if (appointment) {
      // Mode édition — ne pas toucher au titre
      isAutoTitle.current = false;
      setFormData({
        clientId: appointment.clientId,
        startTime: new Date(appointment.startTime),
        endTime: new Date(appointment.endTime),
        duration: appointment.duration,
        title: appointment.title || '',
        description: appointment.description || '',
        notes: appointment.notes || '',
        status: appointment.status
      });
    } else if (initialData) {
      // Mode création avec données initiales — auto-titre si aucun titre fourni
      isAutoTitle.current = !initialData.title;
      const duration = initialData.duration || getDefaultVisitDuration();
      const requestedStartTime = initialData.startTime || new Date();
      const startTime = clampDateToWorkingRangeForSettings(
        roundDateToBookingStep(requestedStartTime),
        workingRange
      );
      const endTime = new Date(startTime.getTime() + duration * 60 * 1000);
      setFormData({
        clientId: initialData.clientId || '',
        startTime,
        endTime,
        duration,
        title: initialData.title || '',
        description: initialData.description || '',
        notes: initialData.notes || ''
      });
    } else {
      // Mode création vide — le titre sera généré dès qu'un client est sélectionné
      isAutoTitle.current = true;
      const now = clampDateToWorkingRangeForSettings(
        roundDateToBookingStep(new Date()),
        workingRange
      );
      const defaultDuration = getDefaultVisitDuration();
      const endTime = new Date(now.getTime() + defaultDuration * 60 * 1000);
      setFormData({
        clientId: '',
        startTime: now,
        endTime: endTime,
        duration: defaultDuration,
        title: '',
        description: '',
        notes: ''
      });
    }
  }, [appointment, initialData, isOpen, workingRange]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setClientSearchQuery('');

    let isMounted = true;
    const selectedClientId = appointment?.clientId || initialData?.clientId;

    const loadClients = async () => {
      setIsLoadingClients(true);
      setClientsError(null);

      try {
        const response = await clientService.getClients({ page: 1, limit: 1000 });
        let availableClients = response.success ? response.data : [];

        if (selectedClientId && !availableClients.some((client) => client.id === selectedClientId)) {
          const selectedClientResponse = await clientService.getClientById(selectedClientId);

          if (selectedClientResponse.success) {
            availableClients = [selectedClientResponse.data, ...availableClients];
          }
        }

        if (isMounted) {
          setClients(availableClients);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('Erreur lors du chargement des clients:', error);
          setClientsError(error.response?.data?.message || 'Impossible de charger la liste des clients');
        }
      } finally {
        if (isMounted) {
          setIsLoadingClients(false);
        }
      }
    };

    loadClients();

    return () => {
      isMounted = false;
    };
  }, [appointment?.clientId, initialData?.clientId, isOpen]);

  useEffect(() => {
    const unsubscribe = subscribeUserPreferences((preferences) => {
      setDurationOptions(preferences.appointmentDurationOptions);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Générer automatiquement le titre quand le client sélectionné change
  useEffect(() => {
    if (!isAutoTitle.current || !formData.clientId || clients.length === 0) {
      return;
    }

    const selectedClient = clients.find((c) => c.id === formData.clientId);
    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        title: `${selectedClient.firstName} ${selectedClient.lastName}`
      }));
    }
  }, [formData.clientId, clients]);

  // Mettre à jour l'heure de fin quand la durée change
  useEffect(() => {
    const newEndTime = new Date(formData.startTime.getTime() + formData.duration * 60 * 1000);
    setFormData(prev => ({
      ...prev,
      endTime: newEndTime
    }));
  }, [formData.startTime, formData.duration]);

  const handleInsertDescriptionTemplate = () => {
    setFormData(prev => ({
      ...prev,
      description: 'Type de visite : \nObjectif : \nMatériel nécessaire : '
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Client requis';
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Heure de début requise';
    }

    if (formData.startTime >= formData.endTime) {
      newErrors.endTime = 'L\'heure de fin doit être après l\'heure de début';
    }

    if (formData.duration <= 0) {
      newErrors.duration = 'Durée invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);

      setRecentClientIds((prev) => {
        const next = [formData.clientId, ...prev.filter((id) => id !== formData.clientId)].slice(0, MAX_RECENT_CLIENTS);
        writeRecentClientIds(next);
        return next;
      });

      onCancel(); // Fermer le modal après succès
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  const formatDateInput = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  const formatTimeInput = (date: Date): string => {
    return format(date, 'HH:mm');
  };

  const handleStartDateChange = (value: string) => {
    if (!value) {
      return;
    }

    const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));

    if (!year || !month || !day) {
      return;
    }

    setFormData((prev) => {
      const newStartTime = new Date(prev.startTime);
      newStartTime.setFullYear(year, month - 1, day);
      const boundedStartTime = clampDateToWorkingRangeForSettings(
        roundDateToBookingStep(newStartTime),
        workingRange
      );

      return {
        ...prev,
        startTime: boundedStartTime
      };
    });
  };

  const handleStartTimeChange = (value: string) => {
    if (!value) {
      return;
    }

    const [hours, minutes] = value.split(':').map((part) => Number.parseInt(part, 10));

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return;
    }

    setFormData((prev) => {
      const newStartTime = new Date(prev.startTime);
      newStartTime.setHours(hours, minutes, 0, 0);
      const boundedStartTime = clampDateToWorkingRangeForSettings(
        roundDateToBookingStep(newStartTime),
        workingRange
      );

      return {
        ...prev,
        startTime: boundedStartTime
      };
    });
  };

  const startTimeValue = formatTimeInput(formData.startTime);
  const startTimeOptionsBase = useMemo(() => buildStartTimeOptions(workingRange), [workingRange]);
  const startTimeOptions = startTimeOptionsBase.includes(startTimeValue)
    ? startTimeOptionsBase
    : [startTimeValue, ...startTimeOptionsBase];

  const handleDurationChange = (value: string) => {
    const duration = Number.parseInt(value, 10);
    if (!Number.isNaN(duration) && duration > 0) {
      setFormData(prev => ({
        ...prev,
        duration
      }));
    }
  };

  const handleDelete = async () => {
    if (!appointment?.id || !onDelete) {
      return;
    }

    if (!globalThis.confirm('Supprimer ce rendez-vous ?')) {
      return;
    }

    try {
      await onDelete(appointment.id);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={appointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Client */}
        <div>
          <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
            Client *
          </label>
          <Input
            id="clientSearch"
            type="text"
            value={clientSearchQuery}
            onChange={(e) => setClientSearchQuery(e.target.value)}
            placeholder="Rechercher par nom, société, ville ou téléphone"
            disabled={isLoading || isLoadingClients}
            className="mb-2"
          />
          {!isLoadingClients && recentClients.length > 0 && !clientSearchQuery.trim() && (
            <div className="mb-2 flex flex-wrap gap-2">
              {recentClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, clientId: client.id }))}
                  className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                    formData.clientId === client.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700'
                  }`}
                >
                  {client.firstName} {client.lastName}
                </button>
              ))}
            </div>
          )}
          <select
            id="clientId"
            value={formData.clientId}
            onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading || isLoadingClients}
          >
            <option value="">{isLoadingClients ? 'Chargement des clients...' : 'Sélectionner un client'}</option>
            {recentClients.length > 0 && (
              <optgroup label="Clients récents">
                {recentClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.firstName} {client.lastName}{client.company ? ` - ${client.company}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {otherFilteredClients.length > 0 && (
              <optgroup label={clientSearchQuery.trim() ? 'Résultats de recherche' : 'Tous les clients'}>
                {otherFilteredClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.firstName} {client.lastName}{client.company ? ` - ${client.company}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {!isLoadingClients && filteredClients.length === 0 && (
              <option value="" disabled>
                Aucun client trouvé
              </option>
            )}
          </select>
          {!isLoadingClients && filteredClients.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} affiché{filteredClients.length > 1 ? 's' : ''}
            </p>
          )}
          {errors.clientId && <p className="text-red-600 text-sm mt-1">{errors.clientId}</p>}
          {clientsError && <p className="text-amber-700 text-sm mt-1">{clientsError}</p>}
        </div>

        {/* Titre */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Titre
          </label>
          <Input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => {
            isAutoTitle.current = false;
            setFormData(prev => ({ ...prev, title: e.target.value }));
          }}
            placeholder="Titre du rendez-vous"
            disabled={isLoading}
          />
        </div>

        {/* Date et heure de début */}
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Date et heure de début *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              id="startDate"
              type="date"
              value={formatDateInput(formData.startTime)}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <select
              id="startTime"
              value={startTimeValue}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            >
              {startTimeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Pas de {CALENDAR_SETTINGS.bookingStepMinutes} minutes | Plage {formatMinutes(workingRange.workdayStartMinutes)}-{formatMinutes(workingRange.workdayEndMinutes)}
            {workingRange.enableMiddayBreak
              ? ` | Pause ${formatMinutes(workingRange.breakStartMinutes)}-${formatMinutes(workingRange.breakEndMinutes)}`
              : ''}
          </p>
          {errors.startTime && <p className="text-red-600 text-sm mt-1">{errors.startTime}</p>}
        </div>

        {/* Durée */}
        <div>
          <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
            Durée (minutes) *
          </label>
          <select
            id="duration"
            value={formData.duration}
            onChange={(e) => handleDurationChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            {durationOptionsForSelect.map((durationOption) => (
              <option key={durationOption} value={durationOption}>
                {formatDurationLabel(durationOption)}
              </option>
            ))}
          </select>
          {errors.duration && <p className="text-red-600 text-sm mt-1">{errors.duration}</p>}
        </div>

        {/* Heure de fin (calculée automatiquement) */}
        <div>
          <label htmlFor="endTimeDisplay" className="block text-sm font-medium text-gray-700 mb-1">
            Heure de fin
          </label>
          <div id="endTimeDisplay" className="px-3 py-2 bg-gray-100 rounded-lg text-gray-700">
            {format(formData.endTime, 'dd/MM/yyyy HH:mm', { locale: fr })}
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            {!formData.description && !appointment && (
              <button
                type="button"
                onClick={handleInsertDescriptionTemplate}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Insérer un modèle
              </button>
            )}
          </div>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Objectif de la visite, éléments à prévoir..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            disabled={isLoading}
          />
        </div>

        {/* Statut (mode édition uniquement) */}
        {appointment && (
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              id="status"
              value={formData.status || 'scheduled'}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as AppointmentFormData['status'] }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            >
              <option value="scheduled">Planifié</option>
              <option value="confirmed">Confirmé</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
              <option value="no_show">Absent</option>
            </select>
          </div>
        )}

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes privées"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            disabled={isLoading}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4">
          <div>
            {appointment?.id && onDelete && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={isLoading}
              >
                Supprimer
              </Button>
            )}
          </div>
          <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
          >
            {appointment ? 'Modifier' : 'Créer'}
          </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AppointmentForm;